// IHOG (Interagency Helicopter Operations Guide) proximity logic.
//
// IHOG mandates two airspace behaviors around an active fire incident:
//
//   1. Contact ring (default 12 NM). Inbound aircraft MUST initiate
//      radio contact with on-scene Air Tactical Group Supervisor
//      (ATGS / "Air Attack") or Helibase Manager by 12 NM out.
//      Purpose: deconflict with retardant ships, tankers orbiting at
//      altitude, helicopters, and ground IC.
//
//   2. Exclusion ring (default 7 NM, but actual fire TFRs vary
//      3-5 NM common, 7 NM aggressive). Inside this radius the pilot
//      MUST NOT approach without explicit clearance from Air Attack.
//      Roughly aligns with the inner ring of an FAA 91.137(a)(2)
//      fire TFR.
//
// Both radii are CONFIGURABLE per incident because the real TFR for
// any given fire may differ from the IHOG defaults. The per-incident
// override lives on the Incident row; this module just consumes the
// resolved radii.
//
// This file is intentionally pure (no React, no zustand) so it tests
// cleanly and so the trigger engine can call it on every GPS fix
// without allocating React state.

import { greatCircleNm } from './geo';
import type { Incident, AviationContact } from '../stores/incidentStore';

/**
 * IHOG defaults from the interagency helicopter operations guide.
 * These should match the values surfaced in Settings.
 */
export const IHOG_DEFAULT_CONTACT_NM   = 12;
export const IHOG_DEFAULT_EXCLUSION_NM = 7;

/**
 * IHOG roles to prefer when picking a "contact this person" recommendation
 * out of an incident's aviation_contacts list. Order matters: try the
 * most specific airspace authority first, then fall back.
 */
const IHOG_PREFERRED_ROLES_ORDERED: ReadonlyArray<string> = [
  'ATGS',           // Air Tactical Group Supervisor — primary
  'Air Attack',     // common synonym used in plain English
  'ASM1',           // Aerial Supervision Module (single-engine variant)
  'ASM2',           // Aerial Supervision Module (twin)
  'Helibase Manager',
  'HMGB',           // helibase manager abbreviation
  'AOBM',           // Air Operations Branch Director (Branch is a level above)
  'AOBD',
];

/**
 * Pick the best "who to contact" from an incident's aviation_contacts.
 * Returns the highest-priority contact that has at least a name OR a
 * phone, ignoring blank rows.
 */
export function pickIhogContact(contacts: ReadonlyArray<AviationContact> | undefined): AviationContact | null {
  if (!contacts || contacts.length === 0) return null;
  // Build a normalized role-key lookup (case-insensitive, trimmed).
  const norm = (s: string | undefined): string => (s ?? '').trim().toLowerCase();
  const preferredLower = IHOG_PREFERRED_ROLES_ORDERED.map((r) => r.toLowerCase());

  for (const target of preferredLower) {
    for (const c of contacts) {
      // Match either the canonical `role` or a user-typed `customLabel`.
      // Both fields are user-editable so both can carry the value.
      if (norm(c.role) === target || norm(c.customLabel) === target) {
        if ((c.name && c.name.trim().length > 0) || (c.phone && c.phone.trim().length > 0)) {
          return c;
        }
      }
    }
  }

  // Fallback: any contact with at least a phone.
  for (const c of contacts) {
    if (c.phone && c.phone.trim().length > 0) return c;
  }
  return null;
}

/**
 * Resolve the contact + exclusion radii for an incident. Per-incident
 * overrides win over the IHOG defaults.
 */
export function resolveRadiiNm(
  incident: Pick<Incident, 'id'> & {
    ihog_contact_nm?: number | null;
    ihog_exclusion_nm?: number | null;
  },
  defaults: { contactNm: number; exclusionNm: number } = {
    contactNm:   IHOG_DEFAULT_CONTACT_NM,
    exclusionNm: IHOG_DEFAULT_EXCLUSION_NM,
  },
): { contactNm: number; exclusionNm: number } {
  const c = sanePositive(incident.ihog_contact_nm,   defaults.contactNm);
  let   x = sanePositive(incident.ihog_exclusion_nm, defaults.exclusionNm);
  // Safety: exclusion must be strictly less than contact, else the
  // contact ring would never fire (entering the contact ring would
  // immediately also enter the exclusion ring). Clamp.
  if (x >= c) x = Math.max(1, c - 1);
  return { contactNm: c, exclusionNm: x };
}

function sanePositive(v: number | null | undefined, fallback: number): number {
  if (v == null) return fallback;
  if (!Number.isFinite(v)) return fallback;
  if (v <= 0) return fallback;
  return v;
}

/**
 * Three-state proximity classification for a single (fix, incident) pair.
 *
 *   'outside'  — beyond the contact ring; no alert
 *   'contact'  — inside contact ring, outside exclusion ring
 *   'exclusion' — inside the exclusion ring
 */
export type IhogZone = 'outside' | 'contact' | 'exclusion';

export interface IhogClassification {
  zone: IhogZone;
  distanceNm: number;
  contactNm: number;
  exclusionNm: number;
}

/**
 * Classify the pilot's distance to a single incident's hazard point.
 * Returns `null` if the incident has no usable lat/lon (we can't
 * compute proximity without a center point).
 */
export function classifyIhogProximity(
  fix:      { lat: number; lon: number },
  incident: Pick<Incident, 'id' | 'latitude' | 'longitude'> & {
    ihog_contact_nm?: number | null;
    ihog_exclusion_nm?: number | null;
  },
): IhogClassification | null {
  if (incident.latitude == null || incident.longitude == null) return null;
  if (!Number.isFinite(incident.latitude) || !Number.isFinite(incident.longitude)) return null;

  const { contactNm, exclusionNm } = resolveRadiiNm(incident);
  const d = greatCircleNm(fix.lat, fix.lon, incident.latitude, incident.longitude);

  let zone: IhogZone = 'outside';
  if (d <= exclusionNm) zone = 'exclusion';
  else if (d <= contactNm) zone = 'contact';

  return { zone, distanceNm: d, contactNm, exclusionNm };
}

/**
 * Evaluate all active incidents and return the closest one in a
 * non-'outside' zone. Used by the trigger engine to fire ONE alert
 * even when the pilot is near several fires at once.
 *
 * Picks 'exclusion' over 'contact' regardless of distance, since
 * exclusion is the more severe state. Within a zone, picks the
 * nearest.
 */
export function pickMostRelevantIhog(
  fix:       { lat: number; lon: number },
  incidents: ReadonlyArray<Pick<Incident, 'id' | 'name' | 'status' | 'latitude' | 'longitude' | 'aviation_contacts'> & {
    ihog_contact_nm?: number | null;
    ihog_exclusion_nm?: number | null;
  }>,
): {
  incident: typeof incidents[number];
  classification: IhogClassification;
} | null {
  let best: { incident: typeof incidents[number]; classification: IhogClassification } | null = null;

  for (const inc of incidents) {
    // Only active incidents trigger alerts. 'staged', 'closed', and
    // 'cancelled' don't have aircraft hazard activity.
    if (inc.status !== 'active') continue;

    const c = classifyIhogProximity(fix, inc);
    if (!c || c.zone === 'outside') continue;

    if (!best) {
      best = { incident: inc, classification: c };
      continue;
    }
    // Exclusion outranks contact.
    if (c.zone === 'exclusion' && best.classification.zone === 'contact') {
      best = { incident: inc, classification: c };
      continue;
    }
    if (c.zone === 'contact' && best.classification.zone === 'exclusion') continue;
    // Same zone — closer wins.
    if (c.distanceNm < best.classification.distanceNm) {
      best = { incident: inc, classification: c };
    }
  }

  return best;
}
